import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { securityAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface ScanCategory {
  score: number;
  status: string;
  issues?: any[];
  certificate?: any;
  headers?: any;
  [key: string]: any;
}

interface ReportData {
  asset: {
    id: number;
    name: string;
    domain: string;
  };
  generated_at: string;
  security_grade: string;
  security_score: number;
  executive_summary: string[];
  statistics: any;
  vulnerability_assessment: {
    overall_score: number;
    risk_level: string;
    categories: { [key: string]: ScanCategory };
    issue_counts?: any;
    recommendations: any[];
    issues?: any[];
  } | null;
  recent_issues: any[];
  scan_history: any[];
  recommendations: any[];
}

interface ScanDetailData {
  scan: any;
  summary: any;
  issues_by_severity: any;
  all_issues: any[];
}

interface IssueDetailData {
  issue: any;
  signature: any;
  remediation: any;
  references: any[];
}

const ScanResultsPage: React.FC = () => {
  useDocumentTitle('Scan Results');
  const navigate = useNavigate();
  const { currentAsset } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<ReportData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Detail modal states
  const [showScanDetailModal, setShowScanDetailModal] = useState(false);
  const [scanDetail, setScanDetail] = useState<ScanDetailData | null>(null);
  const [loadingScanDetail, setLoadingScanDetail] = useState(false);

  // Page and Form issues states
  const [pageIssues, setPageIssues] = useState<any[]>([]);
  const [formIssues, setFormIssues] = useState<any[]>([]);
  const [loadingPageIssues, setLoadingPageIssues] = useState(false);
  const [loadingFormIssues, setLoadingFormIssues] = useState(false);
  const [expandedPage, setExpandedPage] = useState<string | null>(null);

  const [showIssueDetailModal, setShowIssueDetailModal] = useState(false);
  const [issueDetail, setIssueDetail] = useState<IssueDetailData | null>(null);
  const [loadingIssueDetail, setLoadingIssueDetail] = useState(false);

  useEffect(() => {
    if (currentAsset) {
      loadReport();
    }
  }, [currentAsset]);

  const loadReport = async () => {
    if (!currentAsset) return;

    setLoading(true);
    try {
      const response = await securityAPI.getFullReport(currentAsset.id);
      if (response.data.status === 'success') {
        setReport(response.data.data);
      }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Failed to load scan report:', error);
        toast.error('Failed to load scan report');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    if (!currentAsset) return;

    setDownloading(true);
    try {
      const response = await securityAPI.generatePdfReport(currentAsset.id);

      // Create blob from response data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security-report-${currentAsset.id}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF Report downloaded successfully');
    } catch (error: any) {
      console.error('Failed to download report:', error);
      toast.error('Failed to download PDF report');
    } finally {
      setDownloading(false);
    }
  };

  const runNewScan = () => {
    navigate('/security/scanning');
  };

  const printReport = () => {
    window.print();
  };

  // Load scan detail
  const viewScanDetail = async (scanId: number) => {
    setLoadingScanDetail(true);
    setShowScanDetailModal(true);
    try {
      const response = await securityAPI.getScanDetail(scanId);
      if (response.data.status === 'success') {
        setScanDetail(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to load scan details');
      setShowScanDetailModal(false);
    } finally {
      setLoadingScanDetail(false);
    }
  };

  // Load issue detail
  const viewIssueDetail = async (issueId: number) => {
    setLoadingIssueDetail(true);
    setShowIssueDetailModal(true);
    try {
      const response = await securityAPI.getIssueDetail(issueId);
      if (response.data.status === 'success') {
        setIssueDetail(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to load issue details');
      setShowIssueDetailModal(false);
    } finally {
      setLoadingIssueDetail(false);
    }
  };

  const closeScanDetailModal = () => {
    setShowScanDetailModal(false);
    setScanDetail(null);
  };

  const closeIssueDetailModal = () => {
    setShowIssueDetailModal(false);
    setIssueDetail(null);
  };

  // Load page-wise issues
  const loadPageIssues = async () => {
    if (!currentAsset || pageIssues.length > 0) return;
    setLoadingPageIssues(true);
    try {
      const response = await securityAPI.getIssuesByPage(currentAsset.id);
      if (response.data.status === 'success') {
        setPageIssues(response.data.data.pages || []);
      }
    } catch (error) {
      console.error('Failed to load page issues:', error);
    } finally {
      setLoadingPageIssues(false);
    }
  };

  // Load form security issues
  const loadFormIssues = async () => {
    if (!currentAsset || formIssues.length > 0) return;
    setLoadingFormIssues(true);
    try {
      const response = await securityAPI.getFormSecurityIssues(currentAsset.id);
      if (response.data.status === 'success') {
        setFormIssues(response.data.data.issues || []);
      }
    } catch (error) {
      console.error('Failed to load form issues:', error);
    } finally {
      setLoadingFormIssues(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'pages') {
      loadPageIssues();
    } else if (tab === 'forms') {
      loadFormIssues();
    }
  };

  // Get form issue title
  const getFormIssueTitle = (issueType: string) => {
    const titles: Record<string, string> = {
      'missing_csrf': 'Missing CSRF Protection',
      'xss_vulnerable': 'XSS Vulnerability',
      'insecure_action': 'Insecure Form Action',
      'mixed_content': 'Mixed Content',
      'autocomplete_sensitive': 'Sensitive Autocomplete',
    };
    return titles[issueType] || issueType;
  };

  // Score color based on overall score (all metrics combined)
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    if (score >= 50) return 'orange';
    return 'danger';
  };

  // Risk level color
  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel?.toLowerCase()) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'orange';
      case 'critical': return 'danger';
      default: return 'secondary';
    }
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A': case 'A-': return 'success';
      case 'B': return 'info';
      case 'C': return 'warning';
      case 'D': return 'orange';
      case 'F': return 'danger';
      default: return 'secondary';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons: { [key: string]: string } = {
      ssl: 'bi-shield-lock',
      headers: 'bi-file-earmark-text',
      cookies: 'bi-cookie',
      csrf: 'bi-shield-exclamation',
      mixed_content: 'bi-exclamation-triangle',
      information_disclosure: 'bi-eye',
      server_config: 'bi-server',
      cors: 'bi-globe',
      clickjacking: 'bi-window-stack',
      ports: 'bi-diagram-3',
      server_fingerprint: 'bi-fingerprint',
      forms: 'bi-input-cursor-text',
      best_practices: 'bi-check2-circle',
    };
    return icons[category] || 'bi-shield-check';
  };

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      ssl: 'SSL/TLS Certificate',
      headers: 'Security Headers',
      cookies: 'Cookie Security',
      csrf: 'CSRF Protection',
      mixed_content: 'Mixed Content',
      information_disclosure: 'Information Disclosure',
      server_config: 'Server Configuration',
      cors: 'CORS Policy',
      clickjacking: 'Clickjacking Protection',
      ports: 'Port Security',
      server_fingerprint: 'Server Fingerprint',
      forms: 'Form Security',
      best_practices: 'Best Practices',
    };
    return labels[category] || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading scan results...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!report) {
    return (
      <DashboardLayout>
        <div className="container-fluid">
          <div className="text-center py-5">
            <i className="bi bi-shield-x fs-1 text-muted mb-3 d-block"></i>
            <h4>No Scan Results Available</h4>
            <p className="text-muted mb-4">Run a security scan to see detailed results.</p>
            <Link to="/security/scanning" className="btn btn-primary">
              <i className="bi bi-shield-check me-2"></i>Start New Scan
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const vulnAssessment = report.vulnerability_assessment;
  const categories = vulnAssessment?.categories || {};
  const allIssues = vulnAssessment?.issues || [];

  return (
    <DashboardLayout>
      <style>{`
        @media print {
          .sidebar, .navbar, .btn, .nav-tabs, .modal, .breadcrumb {
            display: none !important;
          }
          .container-fluid {
            padding: 0 !important;
            margin: 0 !important;
          }
          .card {
            break-inside: avoid;
            border: 1px solid #dee2e6 !important;
            box-shadow: none !important;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-header {
            display: block !important;
          }
        }
        .print-header {
          display: none;
        }
        /* Custom orange color for medium-high risk scores */
        .text-orange { color: #f97316 !important; }
        .bg-orange { background-color: #f97316 !important; }
        .bg-orange.bg-opacity-10 { background-color: rgba(249, 115, 22, 0.1) !important; }
        .progress-bar.bg-orange { background-color: #f97316 !important; }
        .badge.bg-orange { background-color: #f97316 !important; color: white; }
      `}</style>
      <div className="container-fluid">
        {/* Print Header - Only visible when printing */}
        <div className="print-header mb-4">
          <h1 className="h3 mb-1">Security Scan Report</h1>
          <p className="text-muted mb-0">
            {report?.asset?.name} - Generated on {new Date().toLocaleString()}
          </p>
          <hr />
        </div>
        {/* Header with Actions */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-2">
                <li className="breadcrumb-item">
                  <Link to="/security/scanning" className="text-decoration-none">Security</Link>
                </li>
                <li className="breadcrumb-item active">Scan Results</li>
              </ol>
            </nav>
            <h1 className="h3 mb-1">Security Scan Results</h1>
            <p className="text-muted mb-0">
              {report.asset?.name} - Scanned on {new Date(report.generated_at).toLocaleString()}
            </p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary" onClick={loadReport}>
              <i className="bi bi-arrow-clockwise me-2"></i>Refresh
            </button>
            <button className="btn btn-outline-secondary" onClick={printReport}>
              <i className="bi bi-printer me-2"></i>Print
            </button>
            <button
              className="btn btn-outline-success"
              onClick={downloadReport}
              disabled={downloading}
            >
              {downloading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Downloading...
                </>
              ) : (
                <>
                  <i className="bi bi-download me-2"></i>Download PDF
                </>
              )}
            </button>
            <button className="btn btn-primary" onClick={runNewScan}>
              <i className="bi bi-shield-check me-2"></i>New Scan
            </button>
          </div>
        </div>

        {/* Score Overview Cards */}
        <div className="row g-4 mb-4">
          {/* Main Score */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center py-4">
                <div className={`display-1 fw-bold text-${getScoreColor(report.security_score)}`}>
                  {report.security_score}
                </div>
                <div className="text-muted mb-1">Security Score</div>
                <div className="progress mb-2" style={{ height: '10px' }}>
                  <div
                    className={`progress-bar bg-${getScoreColor(report.security_score)}`}
                    style={{ width: `${report.security_score}%` }}
                  ></div>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted small">out of 100</span>
                  {vulnAssessment?.risk_level && (
                    <span className={`badge bg-${getRiskColor(vulnAssessment.risk_level)} text-capitalize`}>
                      {vulnAssessment.risk_level} Risk
                    </span>
                  )}
                </div>
                <div className="mt-3 pt-2 border-top">
                  <small className="text-muted">
                    Grade: <span className={`fw-bold text-${getGradeColor(report.security_grade)}`}>{report.security_grade}</span>
                    <span className="mx-2">|</span>
                    Based on {Object.keys(categories).length} security categories
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Issue Counts */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="text-muted mb-3">Issues by Severity</h6>
                <div className="row g-3">
                  <div className="col-6">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-danger bg-opacity-10 p-2 me-2">
                        <i className="bi bi-exclamation-triangle-fill text-danger"></i>
                      </div>
                      <div>
                        <div className="fw-bold">{report.statistics?.issues_by_severity?.critical || 0}</div>
                        <small className="text-muted">Critical</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-warning bg-opacity-10 p-2 me-2">
                        <i className="bi bi-exclamation-circle-fill text-warning"></i>
                      </div>
                      <div>
                        <div className="fw-bold">{report.statistics?.issues_by_severity?.high || 0}</div>
                        <small className="text-muted">High</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-info bg-opacity-10 p-2 me-2">
                        <i className="bi bi-info-circle-fill text-info"></i>
                      </div>
                      <div>
                        <div className="fw-bold">{report.statistics?.issues_by_severity?.medium || 0}</div>
                        <small className="text-muted">Medium</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-secondary bg-opacity-10 p-2 me-2">
                        <i className="bi bi-dash-circle-fill text-secondary"></i>
                      </div>
                      <div>
                        <div className="fw-bold">{report.statistics?.issues_by_severity?.low || 0}</div>
                        <small className="text-muted">Low</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <h6 className="text-muted mb-3">Summary</h6>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Total Scans</span>
                  <span className="fw-semibold">{report.statistics?.total_scans || 0}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Total Issues</span>
                  <span className="fw-semibold">{report.statistics?.total_issues_found || 0}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Open Issues</span>
                  <span className="fw-semibold text-danger">{report.statistics?.open_issues || 0}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Resolved</span>
                  <span className="fw-semibold text-success">{report.statistics?.resolved_issues || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Executive Summary */}
        {report.executive_summary && report.executive_summary.length > 0 && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="mb-0">
                <i className="bi bi-clipboard-check me-2 text-primary"></i>
                Executive Summary
              </h5>
            </div>
            <div className="card-body">
              <ul className="mb-0">
                {report.executive_summary.map((item, index) => (
                  <li key={index} className="mb-2">{item}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => handleTabChange('overview')}
            >
              <i className="bi bi-grid me-2"></i>Categories
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'issues' ? 'active' : ''}`}
              onClick={() => handleTabChange('issues')}
            >
              <i className="bi bi-exclamation-triangle me-2"></i>
              All Issues
              {allIssues.length > 0 && (
                <span className="badge bg-danger ms-2">{allIssues.length}</span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'pages' ? 'active' : ''}`}
              onClick={() => handleTabChange('pages')}
            >
              <i className="bi bi-files me-2"></i>By Page
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'forms' ? 'active' : ''}`}
              onClick={() => handleTabChange('forms')}
            >
              <i className="bi bi-input-cursor-text me-2"></i>Form Issues
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'recommendations' ? 'active' : ''}`}
              onClick={() => handleTabChange('recommendations')}
            >
              <i className="bi bi-lightbulb me-2"></i>Recommendations
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => handleTabChange('history')}
            >
              <i className="bi bi-clock-history me-2"></i>History
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="row g-4">
            {Object.entries(categories).map(([key, category]) => (
              <div key={key} className="col-md-6 col-lg-4">
                <div
                  className={`card border-0 shadow-sm h-100 ${expandedCategory === key ? 'border-primary' : ''}`}
                  onClick={() => setExpandedCategory(expandedCategory === key ? null : key)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="d-flex align-items-center">
                        <div className={`rounded-circle bg-${category.status === 'pass' ? 'success' : category.status === 'warning' ? 'warning' : 'danger'} bg-opacity-10 p-2 me-3`}>
                          <i className={`bi ${getCategoryIcon(key)} text-${category.status === 'pass' ? 'success' : category.status === 'warning' ? 'warning' : 'danger'} fs-5`}></i>
                        </div>
                        <div>
                          <h6 className="mb-0">{getCategoryLabel(key)}</h6>
                          <small className="text-muted text-capitalize">{category.status || 'Unknown'}</small>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className={`fw-bold text-${getScoreColor(category.score || 0)}`}>
                          {category.score || 0}/100
                        </div>
                      </div>
                    </div>
                    <div className="progress mb-2" style={{ height: '6px' }}>
                      <div
                        className={`progress-bar bg-${getScoreColor(category.score || 0)}`}
                        style={{ width: `${category.score || 0}%` }}
                      ></div>
                    </div>
                    {category.issues && category.issues.length > 0 && (
                      <div className="mt-2">
                        <small className="text-danger">
                          <i className="bi bi-exclamation-circle me-1"></i>
                          {category.issues.length} issue{category.issues.length !== 1 ? 's' : ''} found
                        </small>
                      </div>
                    )}

                    {/* Expanded content */}
                    {expandedCategory === key && category.issues && category.issues.length > 0 && (
                      <div className="mt-3 pt-3 border-top">
                        {category.issues.slice(0, 3).map((issue: any, idx: number) => (
                          <div key={idx} className="d-flex align-items-start mb-2">
                            <span className={`badge bg-${getSeverityColor(issue.severity)} me-2`}>
                              {issue.severity}
                            </span>
                            <small>{issue.title || issue.description}</small>
                          </div>
                        ))}
                        {category.issues.length > 3 && (
                          <small className="text-muted">+{category.issues.length - 3} more issues</small>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {Object.keys(categories).length === 0 && (
              <div className="col-12 text-center py-5">
                <i className="bi bi-shield-check fs-1 text-success mb-3 d-block"></i>
                <h5>No Categories Scanned</h5>
                <p className="text-muted">Run a scan to see category results.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'issues' && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">
                    <i className="bi bi-exclamation-triangle me-2 text-warning"></i>
                    All Security Issues
                  </h5>
                  <small className="text-muted">Click on any issue to view detailed remediation steps</small>
                </div>
                {allIssues.length > 0 && (
                  <span className="badge bg-danger">{allIssues.length} issues</span>
                )}
              </div>
            </div>
            <div className="card-body p-0">
              {allIssues.length > 0 ? (
                <div className="list-group list-group-flush">
                  {allIssues.map((issue: any, index: number) => (
                    <div
                      key={index}
                      className="list-group-item list-group-item-action px-4 py-3"
                      onClick={() => issue.id && viewIssueDetail(issue.id)}
                      style={{ cursor: issue.id ? 'pointer' : 'default' }}
                    >
                      <div className="d-flex align-items-start">
                        <span className={`badge bg-${getSeverityColor(issue.severity)} me-3`}>
                          {issue.severity?.toUpperCase()}
                        </span>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <h6 className="mb-1">{issue.title || issue.type}</h6>
                            {issue.id && (
                              <i className="bi bi-chevron-right text-muted"></i>
                            )}
                          </div>
                          <p className="text-muted mb-2 small">{issue.description}</p>
                          {issue.url && (
                            <div className="small text-muted mb-1">
                              <i className="bi bi-link-45deg me-1"></i>
                              <span className="text-truncate d-inline-block" style={{ maxWidth: '400px' }}>{issue.url}</span>
                            </div>
                          )}
                          {issue.remediation && (
                            <div className="bg-success bg-opacity-10 border border-success border-opacity-25 p-2 rounded small">
                              <strong className="text-success"><i className="bi bi-tools me-1"></i>Quick Fix:</strong> {issue.remediation}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-shield-check fs-1 text-success mb-3 d-block"></i>
                  <h5>No Issues Found</h5>
                  <p className="text-muted mb-0">Your website passed all security checks.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'pages' && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="mb-0">
                <i className="bi bi-files me-2 text-primary"></i>
                Issues by Page
              </h5>
            </div>
            <div className="card-body p-0">
              {loadingPageIssues ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status"></div>
                  <p className="text-muted mt-2">Loading page issues...</p>
                </div>
              ) : pageIssues.length > 0 ? (
                <div className="list-group list-group-flush">
                  {pageIssues.map((page: any, index: number) => (
                    <div key={index} className="list-group-item">
                      <div
                        className="d-flex justify-content-between align-items-center cursor-pointer"
                        onClick={() => setExpandedPage(expandedPage === page.url ? null : page.url)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center">
                          <i className={`bi ${page.issues_count > 0 ? 'bi-exclamation-circle text-warning' : 'bi-check-circle text-success'} me-2 fs-5`}></i>
                          <div>
                            <div className="fw-medium text-truncate" style={{ maxWidth: '400px' }}>{page.url}</div>
                            <small className="text-muted">Depth: {page.depth} | Forms: {page.forms_count}</small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className={`badge bg-${page.issues_count > 0 ? 'danger' : 'success'}`}>
                            {page.issues_count} issue{page.issues_count !== 1 ? 's' : ''}
                          </span>
                          <i className={`bi bi-chevron-${expandedPage === page.url ? 'up' : 'down'}`}></i>
                        </div>
                      </div>
                      {expandedPage === page.url && page.issues && page.issues.length > 0 && (
                        <div className="mt-3 ms-4 border-start ps-3">
                          {page.issues.map((issue: any, idx: number) => (
                            <div key={idx} className="d-flex align-items-start mb-2 py-2 border-bottom">
                              <span className={`badge bg-${getSeverityColor(issue.severity)} me-2`}>
                                {issue.severity?.toUpperCase()}
                              </span>
                              <div>
                                <div className="fw-medium">{issue.matched_pattern || issue.issue_type}</div>
                                {issue.element_selector && (
                                  <small className="text-muted d-block">Location: {issue.element_selector}</small>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-files fs-1 text-muted mb-3 d-block"></i>
                  <h5>No Page Data Available</h5>
                  <p className="text-muted mb-0">Run a scan with page crawling enabled to see page-wise issues.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'forms' && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="mb-0">
                <i className="bi bi-input-cursor-text me-2 text-primary"></i>
                Form Security Issues
              </h5>
            </div>
            <div className="card-body p-0">
              {loadingFormIssues ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status"></div>
                  <p className="text-muted mt-2">Loading form issues...</p>
                </div>
              ) : formIssues.length > 0 ? (
                <div className="list-group list-group-flush">
                  {formIssues.map((issue: any, index: number) => (
                    <div key={index} className="list-group-item px-4 py-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center">
                          <span className={`badge bg-${getSeverityColor(issue.severity)} me-2`}>
                            {issue.severity?.toUpperCase()}
                          </span>
                          <h6 className="mb-0">{getFormIssueTitle(issue.issue_type)}</h6>
                        </div>
                      </div>
                      <div className="small text-muted mb-2">
                        <div><strong>Page:</strong> {issue.page_url}</div>
                        <div><strong>Form:</strong> {issue.form_selector || issue.form_id || issue.form_name || 'Unknown'}</div>
                        {issue.form_action && <div><strong>Action:</strong> {issue.form_action}</div>}
                        {issue.field_name && <div><strong>Field:</strong> {issue.field_name} ({issue.field_type})</div>}
                      </div>
                      <p className="mb-2">{issue.description}</p>
                      {issue.remediation && (
                        <div className="bg-light p-2 rounded small">
                          <strong className="text-success"><i className="bi bi-tools me-1"></i>Fix:</strong> {issue.remediation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-shield-check fs-1 text-success mb-3 d-block"></i>
                  <h5>No Form Security Issues</h5>
                  <p className="text-muted mb-0">No form security vulnerabilities were detected.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="mb-0">
                <i className="bi bi-lightbulb me-2 text-warning"></i>
                Security Recommendations
              </h5>
              <small className="text-muted">Prioritized actions to improve your security posture</small>
            </div>
            <div className="card-body p-0">
              {report.recommendations && report.recommendations.length > 0 ? (
                <div className="list-group list-group-flush">
                  {report.recommendations.map((rec: any, index: number) => (
                    <div key={index} className="list-group-item px-4 py-3">
                      <div className="d-flex align-items-start">
                        <div className={`rounded-circle bg-${rec.priority === 'critical' ? 'danger' : rec.priority === 'high' ? 'warning' : 'info'} bg-opacity-10 p-2 me-3`}>
                          <i className={`bi ${rec.priority === 'critical' ? 'bi-exclamation-triangle text-danger' : rec.priority === 'high' ? 'bi-exclamation-circle text-warning' : 'bi-lightbulb text-info'}`}></i>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <h6 className="mb-0">{rec.title}</h6>
                            <span className={`badge bg-${rec.priority === 'critical' ? 'danger' : rec.priority === 'high' ? 'warning' : rec.priority === 'medium' ? 'info' : 'secondary'}`}>
                              {rec.priority}
                            </span>
                          </div>
                          <p className="text-muted mb-2">{rec.action}</p>
                          {rec.reference && (
                            <a href={rec.reference} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                              <i className="bi bi-box-arrow-up-right me-1"></i>Learn More
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-shield-check fs-1 text-success mb-3 d-block"></i>
                  <h5>No Recommendations</h5>
                  <p className="text-muted mb-0">No security issues detected. Your website is well configured!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="card border-0 shadow-sm">
            <div className="card-body p-0">
              {report.scan_history && report.scan_history.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Scan ID</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Score</th>
                        <th>Risk</th>
                        <th>Issues</th>
                        <th>Started</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.scan_history.map((scan: any) => (
                        <tr key={scan.id}>
                          <td className="fw-medium">#{scan.id}</td>
                          <td><span className="badge bg-secondary text-capitalize">{scan.type}</span></td>
                          <td>
                            <span className={`badge bg-${scan.status === 'completed' ? 'success' : scan.status === 'failed' ? 'danger' : 'warning'}`}>
                              {scan.status}
                            </span>
                          </td>
                          <td>
                            {scan.overall_score !== null && scan.overall_score !== undefined ? (
                              <span className={`fw-bold text-${getScoreColor(scan.overall_score)}`}>
                                {scan.overall_score}/100
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            {scan.risk_level ? (
                              <span className={`badge bg-${getRiskColor(scan.risk_level)} text-capitalize`}>
                                {scan.risk_level}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            {scan.issues_found > 0 ? (
                              <span className="badge bg-warning text-dark">{scan.issues_found}</span>
                            ) : (
                              <span className="text-success">0</span>
                            )}
                          </td>
                          <td className="small text-muted">{new Date(scan.started_at).toLocaleString()}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => viewScanDetail(scan.id)}
                              title="View Details"
                            >
                              <i className="bi bi-eye"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-success" onClick={downloadReport} title="Download Report">
                              <i className="bi bi-download"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-clock-history fs-1 text-muted mb-3 d-block"></i>
                  <h5>No Scan History</h5>
                  <p className="text-muted mb-0">Run your first scan to see history here.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scan Detail Modal */}
      {showScanDetailModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={closeScanDetailModal}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-shield-check me-2"></i>
                  Scan Details {scanDetail?.scan?.id ? `#${scanDetail.scan.id}` : ''}
                </h5>
                <button type="button" className="btn-close" onClick={closeScanDetailModal}></button>
              </div>
              <div className="modal-body">
                {loadingScanDetail ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="text-muted mt-2">Loading scan details...</p>
                  </div>
                ) : scanDetail ? (
                  <>
                    {/* Scan Info */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <div className="card bg-light border-0">
                          <div className="card-body">
                            <h6 className="text-muted mb-2">Scan Information</h6>
                            <div className="mb-2">
                              <strong>Asset:</strong> {scanDetail.scan.asset_name}
                            </div>
                            <div className="mb-2">
                              <strong>Type:</strong>{' '}
                              <span className="badge bg-secondary text-capitalize">{scanDetail.scan.scan_type}</span>
                            </div>
                            <div className="mb-2">
                              <strong>Status:</strong>{' '}
                              <span className={`badge bg-${scanDetail.scan.status === 'completed' ? 'success' : scanDetail.scan.status === 'running' ? 'warning' : 'danger'}`}>
                                {scanDetail.scan.status}
                              </span>
                            </div>
                            <div className="mb-2">
                              <strong>Started:</strong> {new Date(scanDetail.scan.started_at).toLocaleString()}
                            </div>
                            {scanDetail.scan.completed_at && (
                              <div className="mb-2">
                                <strong>Completed:</strong> {new Date(scanDetail.scan.completed_at).toLocaleString()}
                              </div>
                            )}
                            {scanDetail.scan.duration_seconds && (
                              <div>
                                <strong>Duration:</strong> {scanDetail.scan.duration_seconds}s
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="card bg-light border-0">
                          <div className="card-body">
                            <h6 className="text-muted mb-2">Issue Summary</h6>
                            <div className="row g-2">
                              <div className="col-6">
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-danger me-2">{scanDetail.summary?.critical || 0}</span>
                                  Critical
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-warning text-dark me-2">{scanDetail.summary?.high || 0}</span>
                                  High
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-info me-2">{scanDetail.summary?.medium || 0}</span>
                                  Medium
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-secondary me-2">{scanDetail.summary?.low || 0}</span>
                                  Low
                                </div>
                              </div>
                            </div>
                            <hr />
                            <div className="fw-bold">
                              Total Issues: {scanDetail.summary?.total_issues || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Issues List */}
                    {scanDetail.all_issues && scanDetail.all_issues.length > 0 && (
                      <>
                        <h6 className="mb-3">Found Issues</h6>
                        <div className="list-group">
                          {scanDetail.all_issues.map((issue: any) => (
                            <div
                              key={issue.id}
                              className="list-group-item list-group-item-action d-flex justify-content-between align-items-start"
                              onClick={() => viewIssueDetail(issue.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="ms-2 me-auto">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <span className={`badge bg-${getSeverityColor(issue.severity)}`}>
                                    {issue.severity?.toUpperCase()}
                                  </span>
                                  <strong>{issue.issue_type}</strong>
                                </div>
                                <small className="text-muted d-block">{issue.url}</small>
                                {issue.matched_pattern && (
                                  <small className="text-danger d-block mt-1">
                                    <i className="bi bi-exclamation-circle me-1"></i>
                                    {issue.matched_pattern}
                                  </small>
                                )}
                              </div>
                              <span className={`badge bg-${issue.status === 'resolved' ? 'success' : issue.status === 'investigating' ? 'warning' : 'danger'}`}>
                                {issue.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {(!scanDetail.all_issues || scanDetail.all_issues.length === 0) && (
                      <div className="text-center py-4">
                        <i className="bi bi-shield-check fs-1 text-success mb-2 d-block"></i>
                        <p className="text-muted mb-0">No issues found in this scan</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4 text-muted">No data available</div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeScanDetailModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issue Detail Modal */}
      {showIssueDetailModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={closeIssueDetailModal}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Issue Details
                </h5>
                <button type="button" className="btn-close" onClick={closeIssueDetailModal}></button>
              </div>
              <div className="modal-body">
                {loadingIssueDetail ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="text-muted mt-2">Loading issue details...</p>
                  </div>
                ) : issueDetail ? (
                  <>
                    {/* Issue Header */}
                    <div className="card bg-light border-0 mb-4">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <span className={`badge bg-${getSeverityColor(issueDetail.issue.severity)} me-2`}>
                              {issueDetail.issue.severity?.toUpperCase()}
                            </span>
                            <span className={`badge bg-${issueDetail.issue.status === 'resolved' ? 'success' : issueDetail.issue.status === 'investigating' ? 'warning' : 'secondary'}`}>
                              {issueDetail.issue.status}
                            </span>
                          </div>
                          <small className="text-muted">
                            Detected: {new Date(issueDetail.issue.detected_at).toLocaleString()}
                          </small>
                        </div>
                        <h5 className="mb-2">{issueDetail.issue.issue_type}</h5>
                        <p className="text-muted mb-2">
                          <i className="bi bi-globe me-1"></i>
                          <strong>Asset URL:</strong> {issueDetail.issue.url}
                        </p>
                        {issueDetail.issue.page_url && (
                          <p className="text-muted mb-2">
                            <i className="bi bi-file-earmark me-1"></i>
                            <strong>Found on page:</strong> {issueDetail.issue.page_url}
                          </p>
                        )}
                        {issueDetail.issue.element_selector && (
                          <p className="text-muted mb-0">
                            <i className="bi bi-code-slash me-1"></i>
                            <strong>Element:</strong> <code>{issueDetail.issue.element_selector}</code>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Matched Content */}
                    {issueDetail.issue.matched_pattern && (
                      <div className="mb-4">
                        <h6><i className="bi bi-search me-2"></i>Matched Pattern</h6>
                        <code className="d-block p-3 bg-dark text-light rounded">
                          {issueDetail.issue.matched_pattern}
                        </code>
                      </div>
                    )}

                    {issueDetail.issue.matched_content && (
                      <div className="mb-4">
                        <h6><i className="bi bi-code-slash me-2"></i>Matched Content</h6>
                        <pre className="p-3 bg-light rounded" style={{ maxHeight: '200px', overflow: 'auto', fontSize: '12px' }}>
                          {issueDetail.issue.matched_content}
                        </pre>
                      </div>
                    )}

                    {/* Why This Matters (Justification) */}
                    {issueDetail.remediation?.justification && (
                      <div className="mb-4">
                        <h6><i className="bi bi-info-circle me-2 text-info"></i>Why This Matters</h6>
                        <div className="card border-info bg-info bg-opacity-10">
                          <div className="card-body">
                            <p className="mb-0">{issueDetail.remediation.justification}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Remediation */}
                    {issueDetail.remediation && (
                      <div className="mb-4">
                        <h6><i className="bi bi-tools me-2 text-success"></i>How to Fix</h6>
                        <div className="card border-success">
                          <div className="card-body">
                            <p className="mb-2"><strong>{issueDetail.remediation.summary}</strong></p>
                            <div className="mb-3 d-flex gap-2 flex-wrap">
                              <span className="badge bg-warning text-dark">
                                <i className="bi bi-flag me-1"></i>
                                Priority: {issueDetail.remediation.priority}
                              </span>
                              {issueDetail.remediation.effort && (
                                <span className="badge bg-secondary">
                                  <i className="bi bi-clock me-1"></i>
                                  Effort: {issueDetail.remediation.effort}
                                </span>
                              )}
                            </div>
                            {issueDetail.remediation.steps && issueDetail.remediation.steps.length > 0 && (
                              <>
                                <h6 className="text-muted mb-2">Steps to Resolve:</h6>
                                <ol className="mb-3">
                                  {issueDetail.remediation.steps.map((step: string, idx: number) => (
                                    <li key={idx} className="mb-1">{step}</li>
                                  ))}
                                </ol>
                              </>
                            )}

                            {/* Multiple Code Examples */}
                            {issueDetail.remediation.code_examples && issueDetail.remediation.code_examples.length > 0 && (
                              <div className="mt-3">
                                <h6 className="text-muted mb-2">Code Examples:</h6>
                                {issueDetail.remediation.code_examples.map((example: any, idx: number) => (
                                  <div key={idx} className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <span className="text-muted small">{example.title}</span>
                                      <span className="badge bg-dark">{example.language}</span>
                                    </div>
                                    <pre className="p-3 bg-dark text-light rounded" style={{ fontSize: '12px', whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto' }}>
                                      {example.code}
                                    </pre>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Fallback for single code_example */}
                            {!issueDetail.remediation.code_examples && issueDetail.remediation.code_example && (
                              <>
                                <h6 className="text-muted mb-2">Code Example:</h6>
                                <pre className="p-3 bg-dark text-light rounded" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                                  {issueDetail.remediation.code_example}
                                </pre>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* References */}
                    {issueDetail.references && issueDetail.references.length > 0 && (
                      <div className="mb-4">
                        <h6><i className="bi bi-book me-2"></i>Learn More</h6>
                        <div className="list-group list-group-flush">
                          {issueDetail.references.map((ref: any, idx: number) => (
                            <a
                              key={idx}
                              href={ref.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2"
                            >
                              <span>
                                <i className="bi bi-box-arrow-up-right me-2 text-primary"></i>
                                {ref.title}
                              </span>
                              <span className="badge bg-light text-dark">
                                {ref.url.includes('owasp.org') ? 'OWASP' :
                                 ref.url.includes('mozilla.org') ? 'MDN' :
                                 ref.url.includes('cwe.mitre.org') ? 'CWE' : 'External'}
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {issueDetail.issue.notes && (
                      <div className="mb-4">
                        <h6><i className="bi bi-sticky me-2"></i>Notes</h6>
                        <p className="text-muted">{issueDetail.issue.notes}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4 text-muted">No data available</div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeIssueDetailModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ScanResultsPage;
